// ============================================================================
// FSA OPENING BALANCE VERIFICATION — ANNOTATION MODAL  (ADR-0007)
// ============================================================================
// Status + remark for one comparison difference, opened from the row's
// action menu. A modal (rather than inline editors) gives room for detailed
// remarks and lets future fields join without touching the list UI.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import type {
  ObAnnotationStatus,
  ObComparisonRow,
} from 'api/types/fsa/obVerification.types';

interface Props {
  isOpen: boolean;
  row: ObComparisonRow | null;
  saving: boolean;
  onSave: (ledgerKey: string, status: ObAnnotationStatus, remark: string | null) => void;
  onClose: () => void;
}

const STATUS_OPTIONS = [
  { value: 'UNVERIFIED', label: 'Unverified' },
  { value: 'EXPLAINED', label: 'Explained' },
  { value: 'TO_ADJUST', label: 'To adjust' },
];

const fmt = (v: number | null | undefined): string =>
  v == null ? '—' : v < 0
    ? `(${Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
    : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FsaObAnnotationModal: React.FC<Props> = ({ isOpen, row, saving, onSave, onClose }) => {
  const [status, setStatus] = useState<ObAnnotationStatus>('UNVERIFIED');
  const [remark, setRemark] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const initialSnapshotRef = useRef('');

  useEffect(() => {
    if (!isOpen || !row) return;
    setStatus(row.annotationStatus);
    setRemark(row.annotationRemark ?? '');
    initialSnapshotRef.current = JSON.stringify({ s: row.annotationStatus, r: row.annotationRemark ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify({ s: status, r: remark }) !== initialSnapshotRef.current;
  }, [status, remark]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  if (!isOpen || !row) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-md" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
            <IconSystem.edit size={16} />
            <h2 className="modal-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Verification Note · {row.ledgerName}
            </h2>
          </div>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflow: 'auto' }}>
          {/* difference context */}
          <div style={contextGridStyle}>
            <ContextItem label="Ledger" value={row.ledgerName} />
            <ContextItem label="Parent Group" value={row.parentGroup ?? '—'} />
            <ContextItem
              label="Section"
              value={row.section === 'RESERVE' ? 'Reserves/Capital' : row.section === 'UNCLASSIFIED' ? 'Unclassified' : row.section}
            />
            <ContextItem
              label="Match"
              value={row.matchType === 'MISSING_IN_CLIENT' ? 'Missing in client books'
                : row.matchType === 'NEW_IN_CLIENT' ? 'New in client books'
                : row.matchType === 'SUGGESTED_MATCH' ? `Possible match (${row.matchReason ?? 'suggested'})`
                : 'Matched'}
            />
            {row.matchType === 'SUGGESTED_MATCH' && row.clientLedgerName && (
              <ContextItem label="Client Ledger (suggested)" value={row.clientLedgerName} />
            )}
            <ContextItem label="PY Closing (per FSA)" value={fmt(row.pyAuditedClosing)} numeric />
            <ContextItem label="Client Opening" value={fmt(row.clientOpening)} numeric />
            <ContextItem label="Difference (Δ)" value={fmt(row.delta)} numeric strong />
          </div>

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <SyutSelect
              label="Verification Status"
              value={status}
              onChange={(v) => setStatus((v as ObAnnotationStatus) || 'UNVERIFIED')}
              options={STATUS_OPTIONS}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Remark / Reconciling Note</label>
            <textarea
              className="form-textarea"
              rows={5}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="e.g. PY audit JE for provision not posted by client — rectification entry passed in CY books on 05-Apr…"
              maxLength={2000}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => onSave(row.ledgerKey, status, remark.trim() || null)}
            disabled={saving || !isDirty}
          >
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="Discard the changed status/remark?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const ContextItem: React.FC<{ label: string; value: string; numeric?: boolean; strong?: boolean }> = ({ label, value, numeric, strong }) => (
  <div>
    <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)' }}>{label}</div>
    <div style={{
      fontSize: '0.82rem', fontWeight: strong ? 700 : 600, marginTop: 2, color: 'var(--text-primary)',
      ...(numeric ? { fontVariantNumeric: 'tabular-nums' as const } : {}),
    }}>
      {value}
    </div>
  </div>
);

const contextGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '0.75rem',
  padding: '0.65rem 0.85rem',
  borderRadius: 6,
  border: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};

export default FsaObAnnotationModal;
